/**
 * A query family: one api atom for each request, made by one factory, so no family writes its
 * lifetime, its loads or its changes again.
 *
 * Each member is the paged core of `snapshotAtoms` behind one atom. Thus, each rule of that file
 * holds for a member: it loads when its first reader mounts, a reload keeps the snapshot on
 * screen, a failure arrives as `error`, and the last reader leaving aborts the load in flight, so
 * that load writes nothing. The member takes a `QueryWrite`: a snapshot command goes to the
 * core's own member, `LoadOlder` asks for the older page, and `Apply` gives a change to the
 * family's `apply`. A family without `apply` reads the member again for each change.
 *
 * Two requests with the same key read the same member. The members live in a map in this
 * closure, keyed by the request key. The map holds atom definitions, as the cache of an
 * `atomFamily` does, and each store keeps its own values for them. Two stores live at once for
 * one commit when the Provider key changes, so each entry counts the stores that mount it.
 *
 * A member is removed when its last reader leaves, in each store, so a reader that returns to
 * the request loads it again and the family keeps only what is on screen. The removal waits one
 * tick: strict mode, and a key switch in one commit, unmount a reader and mount it again at
 * once, and that reader must keep its member. A reader that mounts in the wait cancels the
 * removal. A reader that rendered with the member before the removal and mounts after it puts
 * the member back, unless the key has a newer member. The member reads a lifecycle atom, and the
 * `onMount` of that atom sees the readers come and go, so the write of the member takes only a
 * `QueryWrite`. The removal is an atom of its own, because an atom that sets itself from its
 * timer writes its value, not its write. The removal waits while a store mounts the member: the
 * reader of the new store mounts before the timer of the old store fires.
 *
 * A live reader is a reader of the member that also asks for a poll. Polling is part of the
 * request, not of the value: a plain member reader never polls, because a panel that reads a
 * snapshot only to show it must not keep the server busy. The live atom reads the member, so the
 * member loads and stays while only a live reader reads it. While live readers are mounted, the
 * entry runs one interval at the shortest interval of them, and the last live reader leaving
 * clears it, so no timer stays in the store. The timer re-arms only when the shortest interval
 * changes, so a reader that comes and goes does not delay the ticks of a faster one. The entry
 * gives one live atom for each interval, and the removal of the entry drops them.
 *
 * The `olderState` atom of an entry reads the member, so it is a reader of the member: it loads
 * the member and keeps it, as a plain member reader does.
 *
 * Each store has its own removal timer. A timer from one store can fire after a removal in
 * another store gave the key a new member. That timer removes only the member that scheduled it.
 */

import { type Atom, atom, type WritableAtom } from 'jotai';

import {
    ApplyOutcome, type ApplyResult, type LoadState, OlderState, QueryCommand, type QueryWrite,
    SnapshotCommand,
} from '@/types/snapshot';

import { pagedSnapshotAtoms, snapshotMemberAtom } from './snapshotAtoms';

interface QueryOptions<R, S, P, C> {
    getRequestKey(request: R): string;
    /** The snapshot before the first load, of `request` or, for the "nothing" member, of none. */
    getPending(request: R | null): S;
    /** The refresh. `previousSnapshot` gives a tail cursor. */
    load(request: R, previousSnapshot: S | null, signal: AbortSignal): Promise<P>;
    /** Puts a page into the snapshot held at settle. */
    merge(previousSnapshot: S | null, page: P): S;
    /** The older page, for a feed. */
    loadOlder?(request: R, previousSnapshot: S, signal: AbortSignal): Promise<P>;
    apply?(previousSnapshot: S, change: C): ApplyResult<S>;
}

interface QueryFamily<R, S, C> {
    /** The member of `request`: read it for the snapshot; set it with a `QueryWrite`. */
    getMember(request: R | null): WritableAtom<S, [QueryWrite<S, C>], void>;
    /** The same snapshot; while mounted, it polls the member every `intervalMs` or faster. */
    getLive(request: R | null, intervalMs: number): Atom<S>;
    /** The state of the older page of `request`; its reader is also a reader of the member. */
    getOlderState(request: R | null): Atom<OlderState>;
    /** Sends `change` to each member that is held now. */
    applyAll: WritableAtom<null, [C], void>;
}

// The longest delay that the timer API keeps; a longer one fires at once.
const MAX_TIMER_MS = 2_147_483_647;

enum Lifecycle {
    Mount,
    Unmount,
}

enum LiveEvent {
    Add,
    Remove,
}

interface Entry<S, C> {
    member: WritableAtom<S, [QueryWrite<S, C>], void>;
    olderState: Atom<OlderState>;
    getLive(intervalMs: number): Atom<S>;
}

export function queryAtoms<R, S extends LoadState, P, C = never>(
    options: QueryOptions<R, S, P, C>,
): QueryFamily<R, S, C> {
    const entries = new Map<string, Entry<S, C>>();
    let nothingEntry: Entry<S, C> | null = null;

    // The member of no request, for a reader that has nothing to ask yet: the pending snapshot
    // with no load, a write that does nothing, and an idle older page.
    function getNothing(): Entry<S, C> {
        if (nothingEntry === null) {
            const nothing: S = { ...options.getPending(null), isLoading: false };
            const member = atom((): S => nothing, (): void => {});
            nothingEntry = {
                member,
                olderState: atom((): OlderState => OlderState.Idle),
                getLive: (): Atom<S> => member,
            };
        }
        return nothingEntry;
    }

    function createEntry(key: string, request: R): Entry<S, C> {
        const { loadOlder } = options;
        const core = pagedSnapshotAtoms<S, P>(
            options.getPending(request),
            (signal, previousSnapshot) => options.load(request, previousSnapshot, signal),
            (previousSnapshot, page) => options.merge(previousSnapshot, page),
            {
                loadOlder: loadOlder === undefined
                    ? undefined
                    : (signal, previousSnapshot): Promise<P> => (
                        loadOlder(request, previousSnapshot, signal)),
            },
        );
        const inner = snapshotMemberAtom(core);
        let mountedStores = 0;
        const removalTimerAtom = atom<ReturnType<typeof setTimeout> | null>(null);

        const removeAtom = atom(null, (_get, set): void => {
            set(removalTimerAtom, null);
            if (mountedStores === 0 && entries.get(key) === entry) {
                entries.delete(key);
            }
        });

        const lifecycleAtom = atom(
            (get): S => get(inner),
            (get, set, event: Lifecycle): void => {
                switch (event) {
                    case Lifecycle.Mount: {
                        mountedStores += 1;
                        if (!entries.has(key)) {
                            entries.set(key, entry);
                        }
                        const timer = get(removalTimerAtom);
                        if (timer !== null) {
                            clearTimeout(timer);
                            set(removalTimerAtom, null);
                        }
                        break;
                    }
                    case Lifecycle.Unmount:
                        mountedStores -= 1;
                        set(removalTimerAtom, setTimeout(() => {
                            set(removeAtom);
                        }, 0));
                        break;
                    default:
                        break;
                }
            },
        );
        lifecycleAtom.onMount = (dispatch): (() => void) => {
            dispatch(Lifecycle.Mount);
            return (): void => {
                dispatch(Lifecycle.Unmount);
            };
        };

        const memberAtom = atom(
            (get): S => get(lifecycleAtom),
            (_get, set, write: QueryWrite<S, C>): void => {
                switch (write.command) {
                    case SnapshotCommand.Reload:
                    case SnapshotCommand.ReloadNow:
                    case SnapshotCommand.Answer:
                        set(inner, write);
                        break;
                    case QueryCommand.LoadOlder:
                        set(core.olderReload);
                        break;
                    case QueryCommand.Apply: {
                        const { change } = write;
                        set(core.apply, (previousSnapshot: S): ApplyResult<S> =>
                            options.apply === undefined
                                ? { outcome: ApplyOutcome.Reread }
                                : options.apply(previousSnapshot, change));
                        break;
                    }
                    default:
                        break;
                }
            },
        );

        const intervalsAtom = atom<ReadonlySet<number>>(new Set<number>());
        const pollTimerAtom = atom<ReturnType<typeof setInterval> | null>(null);
        const pollMsAtom = atom<number | null>(null);
        const lives = new Map<number, Atom<S>>();

        function createLive(intervalMs: number): Atom<S> {
            const liveAtom = atom(
                (get): S => get(memberAtom),
                (get, set, event: LiveEvent): void => {
                    const intervals = new Set(get(intervalsAtom));
                    switch (event) {
                        case LiveEvent.Add:
                            intervals.add(intervalMs);
                            break;
                        case LiveEvent.Remove:
                            intervals.delete(intervalMs);
                            break;
                        default:
                            break;
                    }
                    set(intervalsAtom, intervals);
                    const nextPollMs = intervals.size === 0 ? null : Math.min(...intervals);
                    if (nextPollMs === get(pollMsAtom)) {
                        return;
                    }
                    const timer = get(pollTimerAtom);
                    if (timer !== null) {
                        clearInterval(timer);
                    }
                    set(pollMsAtom, nextPollMs);
                    set(pollTimerAtom, nextPollMs === null
                        ? null
                        : setInterval(() => {
                            set(core.tick);
                        }, nextPollMs));
                },
            );
            liveAtom.onMount = (dispatch): (() => void) => {
                dispatch(LiveEvent.Add);
                return (): void => {
                    dispatch(LiveEvent.Remove);
                };
            };
            return liveAtom;
        }

        const olderStateAtom = atom((get): OlderState => {
            get(memberAtom);
            return get(core.olderState);
        });

        const entry: Entry<S, C> = {
            member: memberAtom,
            olderState: olderStateAtom,
            getLive: (intervalMs: number): Atom<S> => {
                const existingAtom = lives.get(intervalMs);
                if (existingAtom !== undefined) {
                    return existingAtom;
                }
                const createdAtom = createLive(intervalMs);
                lives.set(intervalMs, createdAtom);
                return createdAtom;
            },
        };
        return entry;
    }

    function getEntry(request: R | null): Entry<S, C> {
        if (request === null) {
            return getNothing();
        }
        const key = options.getRequestKey(request);
        const existingEntry = entries.get(key);
        if (existingEntry !== undefined) {
            return existingEntry;
        }
        const entry = createEntry(key, request);
        entries.set(key, entry);
        return entry;
    }

    return {
        getMember: (request: R | null): WritableAtom<S, [QueryWrite<S, C>], void> =>
            getEntry(request).member,
        getLive: (request: R | null, intervalMs: number): Atom<S> => {
            if (!(intervalMs >= 1 && intervalMs <= MAX_TIMER_MS)) {
                throw new Error(
                    `A live interval is 1 to ${MAX_TIMER_MS} ms, and ${intervalMs} is not.`,
                );
            }
            return getEntry(request).getLive(intervalMs);
        },
        getOlderState: (request: R | null): Atom<OlderState> => getEntry(request).olderState,
        applyAll: atom(null, (_get, set, change: C): void => {
            for (const { member } of entries.values()) {
                set(member, { command: QueryCommand.Apply, change });
            }
        }),
    };
}
